import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

/**
 * This component can be used to display progress towards a target/goal.
 *
 * @example
 * <fcom-progress-indicator
 *  [total]="10000"
 *  [progress]="5000"
 *  [progressText]="'Tier points' | finLocalization"
 *  [targetText]="'Gold tier' | finLocalization"
 *  [fillColor]="loyalty-gold-dark-bg">
 *  {{ progress }} / {{ total }}
 * </fcom-progress-indicator>
 */

@Component({
  selector: 'fcom-progress-indicator',
  templateUrl: './progress-indicator.component.html',
  styleUrls: ['./progress-indicator.component.scss'],
  animations: [
    trigger('fillGrow', [
      state(
        'void',
        style({
          transform: 'scaleX(0)',
        })
      ),
      state(
        '*',
        style({
          transform: 'scaleX({{ progress }})',
        }),
        { params: { progress: 0 } }
      ),
      transition('void => *', animate('1500ms cubic-bezier(0.66, 0, 0.33, 1)')),
      transition('* => *', animate('1500ms cubic-bezier(0.66, 0, 0.33, 1)')),
    ]),
  ],
  standalone: false,
})
export class ProgressIndicatorComponent implements OnInit, OnChanges {
  /**
   * The current value on the indicator
   */
  @Input()
  progress = 0;

  /**
   * The total/target value to achieve
   */
  @Input()
  total = 100;

  /**
   * Optional descriptive label what type of progress is being indicated
   */
  @Input()
  progressText: string;

  /**
   * Optional descriptive label for the type of target to achieve
   */
  @Input()
  targetText: string;

  /**
   * Progress fill color
   */
  @Input()
  fillColor = 'heather-700-bg';

  /**
   * Descriptive aria-label for progress indicator
   */
  @Input({ required: true })
  ariaLabel: string;

  max: boolean;
  percentage: number;
  progressScaleX: number;

  ngOnInit(): void {
    this.max = this.isOverMax();
    this.percentage = this.getPercentage();
    this.onProgressFill();
  }

  ngOnChanges(): void {
    this.max = this.isOverMax();
    this.percentage = this.getPercentage();
    this.onProgressFill();
  }

  private onProgressFill(): void {
    this.progressScaleX = this.max ? 1 : this.getPercentage() * 0.01;
  }

  private isOverMax(): boolean {
    return this.progress > this.total;
  }

  private getPercentage(): number {
    return this.max ? 100 : (this.progress / this.total) * 100;
  }
}
